import type {
  AisleCabinet, CabinetState, ItemRef, RuleLine, Snapshot, Tag, TokenSpec,
} from '../shared/contract';
import {
  BLUTROT, css, INK, NEON, PAPIER, SENF, TAG_COLOR,
} from '../render-kit/src/art/palette';
import { declaredFor, type InputMap } from './input';

/**
 * THE DOM UI — the layer that carries the fantasy (brief priority 5, ART-BIBLE §5/§10).
 *
 * Rules this file obeys, all of them from the bible rather than invented here:
 *
 * - **No hex literal ever appears below.** Every colour comes from the render kit's
 *   palette module, so the DOM and the diorama cannot drift apart across a tuning pass.
 * - **Three typefaces, forever.** Barlow Condensed for signage and rules, Share Tech Mono
 *   for the receipt, the odds inspector and ZINKEN stamps, Monoton for neon only.
 * - **Diegetic where possible.** The Hausregeln board, the help card and the TOPF gauge
 *   belong to the *cabinet* and are drawn as objects screwed to it, not as floating HUD.
 *   The genuinely-abstract things (your purse, your bag, the receipt) are paper.
 * - **This game shows its math.** The odds inspector prints true post-modifier numbers,
 *   sourced from the sim, with the modifier that changed each one named next to it. There
 *   is no secret thumb on the scale anywhere in this game and the UI is where you check.
 */

type El = HTMLElement;

const h = (tag: string, cls?: string, text?: string): El => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

/** Same as `h`, for the tags that can be disabled. */
const hb = (cls?: string, text?: string): HTMLButtonElement => {
  const e = document.createElement('button');
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

/** German thousands dots, matching the receipt's own formatter. */
function fmt(n: number): string {
  const v = Math.round(Math.abs(n)).toString();
  let out = '';
  for (let i = 0; i < v.length; i++) {
    if (i > 0 && (v.length - i) % 3 === 0) out += '.';
    out += v[i];
  }
  return (n < 0 ? '-' : '') + out;
}

const MACHINE_NAME: Record<string, string> = {
  pachinko: 'KUGELREGEN',
  muenzschieber: 'MÜNZSCHIEBER',
  bandit: 'EINARMIGER BANDIT',
  roulette: 'ROULETTE',
  greifer: 'PREISTHEKE',
  flipper: 'DER SAALCHEF',
};

const SOURCE_LABEL: Record<RuleLine['source'], string> = {
  hausregel: 'Hausregel',
  enchant: 'Verzauberung',
  charm: 'Amulett',
  manipulation: 'Manipulation',
  zinken: 'ZINKEN',
};

export interface UiOpts {
  root: El;
  input: InputMap;
  onRestart: () => void;
  /**
   * How many floors this run has. It was hardcoded to 6 in the HUD, which was true of
   * every run that existed at the time and became a lie the moment Die Lehrnacht — one
   * floor, one Abrechnung — was built: the tutorial ended at "ETAGE 1 / 6", promising
   * five more floors that were never coming.
   */
  floors?: number;
}

export class Ui {
  private readonly root: El;
  private readonly input: InputMap;

  private hud!: El;
  private board!: El;
  private beutel!: El;
  private odds!: El;
  private glueck!: El;
  private receipt!: El;
  private toast!: El;
  private aisle!: El;

  private inspecting = false;
  private lastPhase = '';
  private lastFloor = -1;
  private staked = 0;
  private declared: { stream: string; event: string } | null = null;
  /** which machines' help cards the player has opened; the card stays open per machine */
  private readonly helpOpen = new Set<string>();

  constructor(private readonly o: UiOpts) {
    this.root = o.root;
    this.input = o.input;
    this.build();
  }

  // ------------------------------------------------------------------ construction

  private build(): void {
    const wrap = h('div', 'sh-ui');

    // ---- the purse. Abstract by nature (it is in your pocket), so it is paper.
    this.hud = h('div', 'sh-hud');
    wrap.appendChild(this.hud);

    // ---- the aisle strip: where you are, what is lit, what is suspect
    this.aisle = h('div', 'sh-aisle');
    wrap.appendChild(this.aisle);

    // ---- the Hausregeln board: bolted to the cabinet, so it lives cabinet-side
    this.board = h('div', 'sh-board');
    wrap.appendChild(this.board);

    // ---- Der Beutel: a drawer of physical tokens you drag onto the machine
    this.beutel = h('div', 'sh-beutel');
    wrap.appendChild(this.beutel);

    // ---- the odds inspector, on hold
    this.odds = h('div', 'sh-odds');
    wrap.appendChild(this.odds);

    // ---- the GLÜCK meter and its declared-event picker
    this.glueck = h('div', 'sh-glueck');
    wrap.appendChild(this.glueck);

    // ---- the till receipt printer
    this.receipt = h('div', 'sh-receipt');
    wrap.appendChild(this.receipt);

    this.toast = h('div', 'sh-toast');
    wrap.appendChild(this.toast);

    this.root.appendChild(wrap);
  }

  setInspecting(on: boolean): void {
    this.inspecting = on;
    this.odds.classList.toggle('on', on);
  }

  say(text: string): void {
    const line = h('div', 'sh-toast-line', text);
    this.toast.appendChild(line);
    // the deadpan pause is the comic timing unit (ART-BIBLE §7.1 BEAT = 500 ms)
    setTimeout(() => line.classList.add('out'), 2200);
    setTimeout(() => line.remove(), 2900);
    while (this.toast.childElementCount > 5) this.toast.firstElementChild?.remove();
  }

  // ------------------------------------------------------------------ per frame

  render(s: Snapshot): void {
    this.renderHud(s);
    this.renderAisle(s);
    this.renderBoard(s.cabinet);
    this.renderBeutel(s.run.beutel, s.run.items);
    this.renderGlueck(s);
    if (this.inspecting) this.renderOdds(s);

    if (s.run.phase !== this.lastPhase) {
      this.lastPhase = s.run.phase;
      this.root.dataset.phase = s.run.phase;
    }
    if (s.run.floor !== this.lastFloor) {
      this.lastFloor = s.run.floor;
      this.say(`ETAGE ${s.run.floor} · Schuld ${fmt(s.run.debtDue)} Münzen`);
    }
    for (const e of s.events) this.onEvent(e, s);
  }

  private renderHud(s: Snapshot): void {
    const r = s.run;
    const q = r.tickets >= r.ticketQuota;
    this.hud.innerHTML = '';
    this.hud.append(
      stat('MÜNZEN', fmt(r.muenzen), css(SENF.lit)),
      stat('TICKETS', `${fmt(r.tickets)} / ${fmt(r.ticketQuota)}`, q ? css(NEON.gruen.core) : css(PAPIER)),
      stat('GLÜCK', `${r.glueck} / 10`, css(NEON.gruen.core)),
      stat('VERDACHT', String(r.verdacht), verdachtColour(r.verdacht)),
      stat('ETAGE', `${r.floor} / ${this.o.floors ?? 6}`, css(PAPIER)),
      stat('SCHULD', fmt(r.debtDue), css(BLUTROT.lit)),
    );
    if (r.hausverbot) {
      const w = h('div', 'sh-hausverbot', 'HAUSVERBOT — Ihr Bargeld wird nicht mehr angenommen.');
      this.hud.appendChild(w);
    }
  }

  /** The room is the character sheet: what is lit, what is broken, what smells wrong. */
  private renderAisle(s: Snapshot): void {
    this.aisle.innerHTML = '';
    for (const c of s.room) {
      const el = h('button', 'sh-cab');
      el.classList.toggle('dark', !c.powered);
      el.classList.toggle('broken', c.broken);
      el.classList.toggle('here', s.run.activeMachine === c.machine);
      el.append(
        h('span', 'sh-cab-name', MACHINE_NAME[c.machine] ?? c.machine),
        gauge(c.topf01),
        h('span', 'sh-cab-sub', c.broken
          ? 'AUSSER BETRIEB'
          : !c.powered ? 'dunkel'
          : `${c.tokenCount} Marken · Topf ${fmt(c.topf)}`),
      );
      if (c.suspect) {
        // an undiscovered rigging: a crooked "?" stamped on the board, no detail
        const stamp = h('span', 'sh-stamp', '?');
        stamp.title = 'Mit diesem Automaten stimmt etwas nicht. Spielen Sie ihn — oder geben Sie GLÜCK aus, um ihn zu prüfen.';
        el.appendChild(stamp);
      }
      el.addEventListener('click', () => {
        if (s.run.activeMachine === c.machine) this.input.meta({ t: 'leave' });
        else if (c.powered && !c.broken) this.input.meta({ t: 'enter', machine: c.machine });
      });
      this.aisle.appendChild(el);
    }

    // The floor's wonder machine, if one turned up (core-loop.md: at most one per floor).
    if (s.run.wunder) this.aisle.appendChild(this.renderWunder(s));

    const bar = h('div', 'sh-aisle-actions');
    const quota = s.run.tickets >= s.run.ticketQuota;
    bar.append(
      action('Saalchef-Tür', quota, () => this.input.meta({ t: 'boss' }),
        quota ? 'Die Tür geht auf.' : `Sie brauchen ${fmt(s.run.ticketQuota - s.run.tickets)} Tickets mehr.`),
      action('Abrechnung', true, () => this.input.meta({ t: 'descend' }),
        'Der Croupier wartet mit dem Buch. Danach geht es eine Etage tiefer.'),
    );
    this.aisle.appendChild(bar);
  }

  /**
   * THE WONDER MACHINE. One weird one-off per floor, and the two that exist are opposites:
   * the FOTOAUTOMAT sells you a *rollback* and DER WAHRSAGER sells you a *commitment*.
   *
   * Both are paid for in tickets, never coins — economy.md is firm that coins pay debts
   * and tickets buy futures, and a prophecy is the most literal future in the game.
   */
  private renderWunder(s: Snapshot): El {
    const box = h('div', 'sh-wunder');
    const isFoto = s.run.wunder === 'fotoautomat';
    box.append(
      h('div', 'sh-wunder-title', isFoto ? 'FOTOAUTOMAT' : 'DER WAHRSAGER'),
      h('div', 'sh-wunder-sub', isFoto
        ? 'Vier Bilder, sofort fertig. Auch von einem Automaten.'
        : 'Die Zukunft, in Ratenzahlung. Sie wird wahr, weil Sie sie kaufen.'),
    );

    if (isFoto) {
      const held = s.run.fotoHeld;
      const m = s.run.activeMachine;
      box.appendChild(action(
        held ? `Foto einlösen (${MACHINE_NAME[held] ?? held})` : 'Automat fotografieren (10 Tickets)',
        held ? true : !!m,
        () => (held ? this.input.meta({ t: 'einloesen' }) : m && this.input.meta({ t: 'foto', machine: m })),
        held
          ? 'Der Automat kehrt in genau den Zustand zurück, in dem er fotografiert wurde.'
          : 'Treten Sie an einen Automaten und halten Sie ihn fest, wie er ist.',
      ));
      return box;
    }

    if (s.run.wunderCard.length === 0) {
      box.appendChild(h('div', 'sh-hint', 'Die Karte ist leer. Er hat Ihnen heute schon genug erzählt.'));
      return box;
    }
    for (let i = 0; i < s.run.wunderCard.length; i++) {
      const p = s.run.wunderCard[i]!;
      const row = h('button', 'sh-weissagung');
      const afford = s.run.tickets >= p.price;
      row.classList.toggle('too-dear', !afford);
      row.append(
        h('span', 'sh-weissagung-name', p.name),
        h('span', 'sh-weissagung-text', p.text),
        h('span', 'sh-weissagung-price', `${fmt(p.price)} Tickets`),
      );
      row.addEventListener('click', () => {
        if (!afford) { this.say(`Dafür fehlen Ihnen ${fmt(p.price - s.run.tickets)} Tickets.`); return; }
        this.input.meta({ t: 'weissagung', index: i });
      });
      box.appendChild(row);
    }
    return box;
  }

  /**
   * THE HAUSREGELN BOARD — a render of what actually fired, in resolution order, with
   * click-to-source attribution. Not a list of everything that exists: core records a rule
   * only when it genuinely changed an answer on THIS cabinet, which is the whole answer to
   * the genre's stacked-rule-soup failure mode (risk gate R4).
   */
  private renderBoard(cab: CabinetState | null): void {
    this.board.innerHTML = '';
    if (!cab) {
      this.board.classList.add('empty');
      return;
    }
    this.board.classList.remove('empty');
    this.board.append(
      h('div', 'sh-board-title', MACHINE_NAME[cab.machine] ?? cab.machine),
      h('div', 'sh-board-sub', 'HAUSREGELN · in Auflösungsreihenfolge'),
    );

    if (cab.hausregeln.length === 0) {
      this.board.appendChild(h('div', 'sh-rule sh-rule-none', 'Keine besonderen Regeln. Noch nicht.'));
    }
    cab.hausregeln.forEach((r, i) => {
      const row = h('button', 'sh-rule');
      row.classList.add(`src-${r.source}`);
      if (!r.discovered) row.classList.add('hidden-rule');
      row.append(
        h('span', 'sh-rule-n', String(i + 1)),
        h('span', 'sh-rule-text', r.discovered ? r.text : 'Hier stimmt etwas nicht.'),
        h('span', 'sh-rule-src', r.discovered ? SOURCE_LABEL[r.source] : '?'),
      );
      // click-to-source: say who put this rule here
      row.addEventListener('click', () => {
        this.say(r.discovered
          ? `„${r.text}“ — ${SOURCE_LABEL[r.source]}: ${r.sourceId}`
          : 'Ein Zinken. Spielen Sie den Automaten oder prüfen Sie ihn mit GLÜCK.');
      });
      this.board.appendChild(row);
    });

    // The help card: the machine's declared tag table, printed, exactly as machines.md
    // demands. Its emptiness on the physics cabinets is the proof that the grammar works.
    //
    // It is a <details> and it is SHUT by default, which is a framing decision rather
    // than a tidiness one. Measured on the 900x560 gate viewport, the DOM was covering
    // 53 % of the screen and the largest single contributor was this card — so the game
    // was mostly a picture of a rules panel with an arcade behind it. The card is
    // reference material you consult once per machine; the machine is the thing you came
    // to look at. It remembers being opened, per machine, for the rest of the session.
    const card = document.createElement('details');
    card.className = 'sh-help';
    card.open = this.helpOpen.has(cab.machine);
    const sum = document.createElement('summary');
    sum.className = 'sh-help-title';
    sum.textContent = `HILFEKARTE · was dieser Automat mit Marken anfängt (${cab.helpCard.length})`;
    card.appendChild(sum);
    card.addEventListener('toggle', () => {
      if (card.open) this.helpOpen.add(cab.machine);
      else this.helpOpen.delete(cab.machine);
    });
    if (cab.helpCard.length === 0) {
      card.appendChild(h('div', 'sh-help-row', 'Nichts Erklärtes. Hier zählt nur Physik.'));
    }
    for (const line of cab.helpCard) {
      const row = h('div', 'sh-help-row');
      row.append(tagChip(line.tag), h('span', undefined, line.note));
      card.appendChild(row);
    }
    card.appendChild(h('div', 'sh-help-limit', `GRENZE: ${cab.limitLabel}`));
    this.board.appendChild(card);

    this.board.appendChild(meterRow('TOPF', cab.topf01, `${fmt(cab.topf)} / ${fmt(cab.topfMax)}`));
    this.board.appendChild(meterRow('TILT', cab.tilt01, cab.lockoutUntil > 0 ? 'GESPERRT' : ''));
    this.board.appendChild(meterRow('STRUKTUR', cab.limit01, cab.broken ? 'GEBORSTEN' : ''));
    this.board.appendChild(h('div', 'sh-board-foot',
      `STRÄHNE ×${cab.straehne} · Einsatzstufe ${cab.stakeLevel} · nächster Eintritt ${fmt(cab.buyIn)} Münzen`));
  }

  /**
   * DER BEUTEL — your deck, as physical objects. Enchantments must be readable at a
   * glance, so every token wears its tags as coloured pips in the contract's tag order
   * (the same order the render kit degrades by), never as a tooltip you have to hunt for.
   */
  private renderBeutel(bag: TokenSpec[], items: ItemRef[]): void {
    this.beutel.innerHTML = '';
    this.beutel.appendChild(h('div', 'sh-drawer-title', 'DER BEUTEL'));
    const row = h('div', 'sh-tokens');
    bag.forEach((t, i) => {
      const el = h('button', `sh-token mat-${t.material}`);
      el.classList.toggle('staked', i === this.staked);
      el.draggable = true;
      el.append(
        h('span', 'sh-token-face', fmt(t.faceValue)),
        h('span', 'sh-token-mat', t.material),
      );
      const pips = h('span', 'sh-pips');
      for (const tag of t.tags) pips.appendChild(tagChip(tag, true));
      el.appendChild(pips);
      el.title = `${t.material} · Nennwert ${fmt(t.faceValue)}${t.tags.length ? ' · ' + t.tags.join(', ') : ''}`;
      const pick = (): void => {
        this.staked = i;
        this.input.aim.beutel = i;
        this.renderBeutel(bag, items);
      };
      el.addEventListener('click', pick);
      // drag a token onto the machine to stake it — the diegetic version of "press space"
      el.addEventListener('dragstart', (ev) => {
        pick();
        (ev as DragEvent).dataTransfer?.setData('text/plain', String(i));
      });
      row.appendChild(el);
    });
    this.beutel.appendChild(row);

    if (items.length > 0) {
      this.beutel.appendChild(h('div', 'sh-drawer-title', 'AMULETTE & ZINKEN'));
      const ir = h('div', 'sh-items');
      for (const it of items) {
        const el = h('div', `sh-item kind-${it.kind}`);
        el.append(h('span', 'sh-item-name', it.name), h('span', 'sh-item-kind', SOURCE_LABEL[it.kind]));
        if (it.kind === 'zinken' && it.discovered === false) el.classList.add('hidden-rule');
        ir.appendChild(el);
      }
      this.beutel.appendChild(ir);
    }
  }

  /**
   * THE GLÜCK METER with its declared-event picker. GLÜCK bends ONE DECLARED upcoming
   * event, and declaring it is the decision — a button that silently picked something for
   * you would turn a tool into a slot machine.
   */
  private renderGlueck(s: Snapshot): void {
    const m = s.run.activeMachine;
    this.glueck.innerHTML = '';
    this.glueck.appendChild(h('div', 'sh-drawer-title', 'GLÜCK'));
    const pips = h('div', 'sh-glueck-pips');
    for (let i = 0; i < 10; i++) {
      const p = h('span', 'sh-glueck-pip');
      p.classList.toggle('on', i < s.run.glueck);
      pips.appendChild(p);
    }
    this.glueck.appendChild(pips);

    if (!m) {
      this.glueck.appendChild(h('div', 'sh-hint', 'An einem Automaten können Sie GLÜCK auf ein angesagtes Ereignis setzen.'));
      return;
    }
    const d = declaredFor(m);
    this.declared = { stream: d.stream, event: d.event };
    const btn = hb('sh-glueck-spend', `1 GLÜCK auf: ${d.label}`);
    btn.disabled = s.run.glueck < 1;
    btn.addEventListener('click', () => {
      if (!this.declared) return;
      this.input.glueck(this.declared.stream, this.declared.event);
      this.say(`GLÜCK angesagt: ${d.label}`);
    });
    this.glueck.appendChild(btn);
    this.glueck.appendChild(h('div', 'sh-hint',
      'Angesagt heißt angesagt: der nächste Wurf dieses Ereignisses wird einmal neu gewürfelt.'));
  }

  /**
   * THE ODDS INSPECTOR — hold I (or Shift). economy.md: "all odds are inspectable — the
   * anti-gambling-industry stance as a feature: *this* house shows its math."
   *
   * These are the post-modifier numbers the sim will actually use, read out of the sim's
   * own state, not a re-derivation that could drift from it.
   */
  private renderOdds(s: Snapshot): void {
    const cab = s.cabinet;
    this.odds.innerHTML = '';
    this.odds.appendChild(h('div', 'sh-odds-title', 'ECHTE QUOTEN · nach allen Modifikatoren'));
    if (!cab) {
      this.odds.appendChild(h('div', 'sh-odds-row', 'Treten Sie an einen Automaten.'));
      return;
    }
    const rows: [string, string][] = [
      ['Automat', MACHINE_NAME[cab.machine] ?? cab.machine],
      ['STRÄHNE', `×${cab.straehne}`],
      ['TOPF', `${fmt(cab.topf)} von ${fmt(cab.topfMax)} (${Math.round(cab.topf01 * 100)} %)`],
      ['Etagenfaktor', `×${(1 + 0.5 * (s.run.floor - 1)).toFixed(2)}`],
      ['Struktur', `${Math.round(cab.limit01 * 100)} % — ${cab.limitLabel}`],
      ['TILT', `${Math.round(cab.tilt01 * 100)} %${cab.lockoutUntil > 0 ? ` (gesperrt bis Tick ${cab.lockoutUntil})` : ''}`],
      ['Einsatzstufe', `${cab.stakeLevel} · Eintritt ${fmt(cab.buyIn)}`],
      ['Wirksame Regeln', String(cab.hausregeln.filter((r) => r.discovered).length)],
    ];
    for (const [k, v] of rows) {
      const row = h('div', 'sh-odds-row');
      row.append(h('span', 'sh-odds-k', k), h('span', 'sh-odds-v', v));
      this.odds.appendChild(row);
    }
    for (const r of cab.hausregeln) {
      const row = h('div', 'sh-odds-row rule');
      row.append(
        h('span', 'sh-odds-k', r.discovered ? SOURCE_LABEL[r.source] : 'ZINKEN'),
        h('span', 'sh-odds-v', r.discovered ? r.text : 'unbekannt — noch nicht entdeckt'),
      );
      this.odds.appendChild(row);
    }
  }

  /** THE RECEIPT PRINTER. Death is a receipt; it prints a line at a time on a till roll. */
  printReceipt(lines: string[], won: boolean): void {
    this.receipt.innerHTML = '';
    this.receipt.classList.add('on');
    this.receipt.classList.toggle('lost', !won);
    const roll = h('pre', 'sh-roll');
    this.receipt.appendChild(roll);
    let i = 0;
    const step = (): void => {
      if (i >= lines.length) {
        const again = h('button', 'sh-again', 'NOCH EINE NACHT');
        again.addEventListener('click', () => this.o.onRestart());
        this.receipt.appendChild(again);
        this.receipt.dataset.done = '1';
        return;
      }
      roll.textContent += (i > 0 ? '\n' : '') + lines[i++];
      // RATCHET = 55 ms, one ticket step (ART-BIBLE §7.1) — the till prints in detents
      setTimeout(step, 55);
    };
    step();
  }

  private onEvent(e: Snapshot['events'][number], s: Snapshot): void {
    switch (e.t) {
      case 'receipt':
        this.printReceipt(e.lines, s.run.won);
        break;
      case 'zinkenDiscovered':
        this.say(`Zinken entdeckt: ${e.zinken} an ${MACHINE_NAME[e.machine] ?? e.machine}`);
        break;
      case 'zinkenStolen':
        this.say(`Brecheisen angesetzt. ${e.zinken} gehört jetzt Ihnen.`);
        break;
      case 'item':
        this.say(`${e.gained ? 'Erhalten' : 'Verloren'}: ${e.name}`);
        break;
      case 'grab':
        if (e.prize) this.say(e.kept ? `Gegriffen: ${e.prize}` : `Abgerutscht: ${e.prize}`);
        break;
      case 'bankBreak':
        this.say(`BANK GESPRENGT — ${fmt(e.payout)} Münzen.`);
        break;
      case 'tiltLockout':
        this.say('TILT. Der Automat sperrt.');
        break;
      case 'hausverbot':
        this.say(e.active ? 'HAUSVERBOT: Ihr Bargeld wird nicht angenommen.' : 'Das Hausverbot ist aufgehoben.');
        break;
      case 'rausschmeisser':
        if (e.phase === 'arrive') this.say('Der Rausschmeißer lehnt sich an den Automaten.');
        if (e.phase === 'win') this.say('Er lässt ab. VERDACHT −30.');
        if (e.phase === 'lose') this.say('Er nimmt Ihre beste Marke mit.');
        break;
      case 'quotaMet':
        this.say('Die Saalchef-Tür geht auf.');
        break;
      case 'bossPhase':
        this.say(`Phase ${e.phase}: ${e.rule}`);
        break;
      case 'bossEnd':
        this.say(e.won ? `${e.boss} geschlagen. Die Trophäe ist seine Regel.` : `${e.boss} bleibt sitzen.`);
        break;
      case 'abrechnung':
        this.say(e.confiscated.length > 0
          ? `Abrechnung: ${fmt(e.paid)} von ${fmt(e.due)}. Der Croupier nimmt ${e.confiscated.length} Dinge mit.`
          : `Abrechnung beglichen: ${fmt(e.paid)} Münzen.`);
        break;
      default:
        break;
    }
  }
}

// ---------------------------------------------------------------- small parts

function stat(label: string, value: string, colour: string): El {
  const e = h('div', 'sh-stat');
  const v = h('span', 'sh-stat-v', value);
  v.style.color = colour;
  e.append(h('span', 'sh-stat-k', label), v);
  return e;
}

function gauge(v01: number): El {
  const e = h('span', 'sh-gauge');
  const fill = h('span', 'sh-gauge-fill');
  fill.style.width = `${Math.round(Math.max(0, Math.min(1, v01)) * 100)}%`;
  e.appendChild(fill);
  return e;
}

function meterRow(label: string, v01: number, note: string): El {
  const e = h('div', 'sh-meter');
  e.append(h('span', 'sh-meter-k', label), gauge(v01), h('span', 'sh-meter-note', note));
  if (v01 >= 1) e.classList.add('full');
  return e;
}

function tagChip(tag: Tag, small = false): El {
  const e = h('span', small ? 'sh-pip' : 'sh-tag', small ? '' : tag);
  e.style.background = css(TAG_COLOR[tag] ?? INK);
  e.title = tag;
  return e;
}

function action(label: string, enabled: boolean, on: () => void, hint: string): El {
  const b = hb('sh-action', label);
  b.disabled = !enabled;
  b.title = hint;
  b.addEventListener('click', on);
  return b;
}

function verdachtColour(v: number): string {
  if (v >= 90) return css(BLUTROT.lit);
  if (v >= 60) return css(NEON.violet.core);
  if (v >= 30) return css(SENF.lit);
  return css(PAPIER);
}
